import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';
import { Subscription } from 'rxjs';

import { DropdownTypesEnum } from '../../../../Common/Forms/enums/dropdown-types.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { NetworkTypeEnum } from '../../../Core/enums/network-type.enum';
import { SearchCriteriaSortFieldEnum } from '../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { AggregateFieldsApiEnum } from '../../../../api/mentions/enum/aggregate-fields-api.enum';
import { AggregateFunctionApiEnum } from '../../../../api/mentions/enum/aggregate-function-api.enum';
import { ReportSeriesMentionRequestGeneratorOutput } from '../../../../Common/Reports/models/report-series-mention-request-generator-output.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { ChartExportEventModel } from '../../../../Common/Charts/models/chart-export-event.model';
import { SortOrderEnum } from '../../../../api/shared/enums/sort-order.enum';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { AbstractDashboardSearchService } from '@Monitor/Core/services/abstract-dashboard-search.service';
import { networkTypeToMediaTypeModelMap } from '@Social/Intelligence/models/network-type-to-media-type-model.map';
import { SocialChartExportService } from '@Social/Intelligence/services/social-chart-export.service';


/**
 * Social wrapper component top posts
 */
@Component({
    selector: 'agpr-top-posts',
    templateUrl: './top-posts.component.html',
    styleUrls: ['./top-posts.component.scss'],
    standalone: false
})
export class TopPostsComponent implements OnInit, OnDestroy {

    readonly messageSeverities = messageSeverityEnum;
    readonly dropdownTypes = DropdownTypesEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;

    public options: { label: string; value: ReportSeriesMentionRequestGeneratorOutput }[] = [];

    seriesMentionSubscription: Subscription;

    @Input() id: string = '';

    @Input() get series(): ReportSeriesModel {
        return this._series;
    }

    @Input() set enableEngagementInsights(enableEngagementInsights: boolean) {
        this._enableEngagementInsights = enableEngagementInsights;
        this.prepareSortOptions();
    };

    _enableEngagementInsights: boolean = true;

    set series(value: ReportSeriesModel) {
        this._series = value;
        this.seriesMentionSubscription?.unsubscribe();
        this.seriesMentionSubscription = this.series.mentionProvider.subscribe(
            mentions => {
                this.mentions = mentions;
            }
        );
    }
    protected _series: ReportSeriesModel = null;

    @Input() set excludedNetworkTypes(networkTypes: NetworkTypeEnum[]) {
        this.showReddit = !networkTypes?.includes(NetworkTypeEnum.reddit);
    };
    @Input() availableNetworkTypes: NetworkTypeEnum[] = [];
    @Input() title: string = 'Social.intelligence.charts.topPosts.title';
    @Input() sortIndex: number = 0;

    @Output() seriesChanged = new EventEmitter<ReportSeriesModel>();

    currentSort: DropdownOptionModel<ReportSeriesMentionRequestGeneratorOutput>;
    currentSocialType: NetworkTypeEnum = NetworkTypeEnum.all;
    mentions: Mention[] = null;
    showReddit: boolean = true;

    public buttonTypesEnum = ButtonTypesEnum;

    constructor(
        private localization: LocalizationService,
        @Inject('SocialSeriesService') private socialSeriesService: AbstractDashboardSearchService,
        public el: ElementRef,
        private chartExportService: SocialChartExportService,
    ) { }

    ngOnInit(): void {
        this.prepareSortOptions();
    }

    ngOnDestroy(): void {
        this.seriesMentionSubscription?.unsubscribe();
    }

    setSocialTypes(selectedType: NetworkTypeEnum) {
        this.currentSocialType = selectedType;

        this.socialSeriesService.deregisterSeries([this.series]);
        this.series.mentions.filters.searchCriteria.query.includedMediaTypes = networkTypeToMediaTypeModelMap.get(this.currentSocialType);
        this.socialSeriesService.fulfillSeries([this.series]);

        this.seriesChanged.next(this.series);
    }

    onSortChange(sortOrder: DropdownOptionModel<ReportSeriesMentionRequestGeneratorOutput>) {
        this.currentSort = sortOrder;

        this.socialSeriesService.deregisterSeries([this.series]);

        this.series.mentions.mentionRequestGenerator = () => {
            return this.currentSort.value;
        };
        this.socialSeriesService.fulfillSeries([this.series]);

        this.seriesChanged.next(this.series);
    }

    onViewAll(event: any) {
        // TODO: Do something...
    }

    prepareSortOptions(): void {
        // Impressions -> SearchCriteriaSortFieldEnum.audience_reach
        // Engagement -> SearchCriteriaSortFieldEnum.engagement_total
        // Amplification => SearchCriteriaSortFieldEnum.share
        // Conversation => Replies & Quotes -> aka sortFunction
        // Applause => SearchCriteriaSortFieldEnum.like
        if (this._enableEngagementInsights) {
            this.options = [
                {
                    label: this.localization.transform(`Social.intelligence.charts.topPosts.${SearchCriteriaSortFieldEnum.engagement_total}`),
                    value: {
                        sortOrder: SortOrderEnum.descending,
                        sortField: SearchCriteriaSortFieldEnum.engagement_total,
                        pageSize: 5
                    }
                },
                {
                    label: this.localization.transform(`Social.intelligence.charts.topPosts.${SearchCriteriaSortFieldEnum.audience_reach}`),
                    value: {
                        sortOrder: SortOrderEnum.descending,
                        sortField: SearchCriteriaSortFieldEnum.audience_reach,
                        pageSize: 5
                    }
                },
                {
                    label: this.localization.transform(`Social.intelligence.charts.topPosts.${SearchCriteriaSortFieldEnum.share}`),
                    value: {
                        sortOrder: SortOrderEnum.descending,
                        sortField: SearchCriteriaSortFieldEnum.share,
                        pageSize: 5
                    }
                },
                {
                    label: this.localization.transform(`Social.intelligence.charts.topPosts.${SearchCriteriaSortFieldEnum.quote}`),
                    value: {
                        sortOrder: SortOrderEnum.descending,
                        sortField: SearchCriteriaSortFieldEnum.function,
                        sortFunction: {
                            aggregateFunction: AggregateFunctionApiEnum.SUM,
                            aggregateFields: [AggregateFieldsApiEnum.QUOTE, AggregateFieldsApiEnum.REPLY],
                        },
                        pageSize: 5
                    },
                },
                {
                    label: this.localization.transform(`Social.intelligence.charts.topPosts.${SearchCriteriaSortFieldEnum.like}`),
                    value: {
                        sortOrder: SortOrderEnum.descending,
                        sortField: SearchCriteriaSortFieldEnum.like,
                        pageSize: 5
                    }
                }
            ];
        } else {
            this.options = [
                {
                    label: this.localization.transform(`Social.intelligence.charts.topPosts.${SearchCriteriaSortFieldEnum.audience_reach}`),
                    value: {
                        sortOrder: SortOrderEnum.descending,
                        sortField: SearchCriteriaSortFieldEnum.audience_reach,
                        pageSize: 5
                    }
                }
            ];
        }

        if (this.sortIndex <= this.options.length - 1) {
            this.currentSort = this.options[this.sortIndex];
        } else {
            this.currentSort = this.options[0];
        }
    }

    // @HACK: have to pass chartElementRef from html template or else it would not export the image properly for some charts
    public exportChart(event: ChartExportEventModel, chartElementRef: any) {
        const exportTitle = this.localization.transform(this.title) + ' ' + this.currentSort.label;
        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        const exportMenuElement = chartElementRef.querySelector('.agpr-topPosts-exportMenu');
        const expandPostIconElement = chartElementRef.querySelectorAll('.agpr-postFeed-additionalInfo-expandToggle');
        const dropdownIconElement = chartElementRef.querySelector('.agpr-dropdown-triggerIcon');
        const metadataLabelElement = chartElementRef.querySelectorAll('.agpr-postFeed-metadataLabel');
        const badgeElement = chartElementRef.querySelectorAll('.agpr-postFeed-additionalInfo-badgeWrapper');

        // Hide some fontawsome icons and restyle metadata label so the exported image looks good
        exportMenuElement.style.display = 'none';
        expandPostIconElement.forEach(element => {
            element.style.display = 'none';
        });
        dropdownIconElement.style.display = 'none';
        metadataLabelElement.forEach(element => {
            element.style.marginTop = '20px';
        });
        badgeElement.forEach(element => {
            element.style.display = 'none';
        });

        this.chartExportService.exportImage(exportType, chartElementRef, exportTitle).then(() => {
            exportMenuElement.style.display = 'block';
            expandPostIconElement.forEach(element => {
                element.style.display = 'block';
            });
            dropdownIconElement.style.display = 'unset';
            metadataLabelElement.forEach(element => {
                element.style.marginTop = '0px';
            });
            badgeElement.forEach(element => {
                element.style.display = 'block';
            });
        });
    }

}
